import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import Sidebar from "../sidebar/sidebar";
import Header from "../header/header";
import colors from "../../design/colors";
import styled from "styled-components";
import Menu from "../icons/menu";
import { deviceWidth } from "@/design/defaultTheme";
import { useWindowSize } from "@/hooks/useWindowSize";

interface LayoutProps {
  children: React.ReactNode;
}

const Layout = ({ children }: LayoutProps) => {
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { width } = useWindowSize();
  const isMobile = width ? width < deviceWidth.mobile : false;
  const isTablet = width ? width <= deviceWidth.tablet : false;

  const blankHeaderRoutes = ["/login", "/signup", "/find"];
  const noHeaderRoutes = ["/landing"];
  const noSidebarRoutes = ["/login", "/signup", "/landing", "/find"];

  const noHeaderRegex = /^\/study\/exam\/\d+\/ing$/;
  const noSidebarRegex = /^\/study\/exam\/\d+\/ing$/;

  const showBlankHeader = blankHeaderRoutes.some((path) =>
    location.pathname.startsWith(path)
  );
  const noHeader =
    noHeaderRoutes.some((path) => location.pathname.startsWith(path)) ||
    noHeaderRegex.test(location.pathname);

  const showSidebar =
    !noSidebarRoutes.some((path) => location.pathname.startsWith(path)) &&
    !noSidebarRegex.test(location.pathname);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  return (
    <LayoutContainer>
      {showSidebar && (
        <>
          <Sidebar
            isOpen={isSidebarOpen || (!isMobile && !isTablet)}
            onClose={() => setIsSidebarOpen(false)}
          />
          {(isMobile || isTablet) && !isSidebarOpen && (
            <MobileToggleButton onClick={toggleSidebar}>
              <Menu
                width={isMobile ? "16px" : "24px"}
                height={isMobile ? "16px" : "24px"}
                color={colors.gray900}
              />
            </MobileToggleButton>
          )}
        </>
      )}

      <ContentWrapper $showSidebar={showSidebar && !isMobile && !isTablet}>
        {!showBlankHeader && !noHeader && <Header />}
        <main
          style={{
            height:
              showBlankHeader || !noHeader
                ? isMobile || isTablet
                  ? "calc(100% - 50px)"
                  : "calc(100% - 80px)"
                : "100%",
            width: "100%",
            backgroundColor: colors.white,
            position: "relative",
            display: "flex",
            flexDirection: "column",
            overflow: isMobile || isTablet ? undefined : "hidden",
            overflowY: isMobile || isTablet ? "auto" : undefined,
            WebkitOverflowScrolling: "touch",
          }}
        >
          {children}
        </main>
      </ContentWrapper>
    </LayoutContainer>
  );
};

export default Layout;

/* ---------------------------------
 * Styled Components
 * --------------------------------- */

const LayoutContainer = styled.div`
  display: flex;
  flex-direction: row;
  overflow: hidden;
  position: relative;
  background-color: ${colors.white};
  padding-bottom: env(safe-area-inset-bottom, 0px);
`;

const ContentWrapper = styled.div<{ $showSidebar: boolean }>`
  flex: 1;
  min-width: 0;
  height: 100vh;
  height: 100dvh;
  margin-left: ${({ $showSidebar }) => ($showSidebar ? "230px" : "0")};

  ${({ theme }) => theme.device.mobile} {
    margin-left: 0;
  }

  ${({ theme }) => theme.device.tablet} {
    margin-left: 0;
  }
`;

const MobileToggleButton = styled.button`
  display: none;
  position: fixed;
  top: 20px;
  left: 20px;
  z-index: 999;
  background: ${colors.white};
  border: none;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  ${({ theme }) => theme.device.mobile} {
    display: block;
    top: 8px;
    left: 10px;
  }

  ${({ theme }) => theme.device.tablet} {
    display: block;
    top: 20px;
    left: 40px;
  }
`;
